import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, KeyRound, Plus, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type Paginated } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
  type BadgeTone,
} from '../components/ui';
import { useClientOptions } from './Invoices';
import { useSubscriptionProjects } from './Subscriptions';

/**
 * Finance › Settings › External systems (2026-10-28; spec §4.5, §8) — the
 * systems outside Viganium that use Finance's integration API: a product's own
 * customer portal, say. Each is granted the internal projects it may reach,
 * the clients it may represent and the scopes its tokens may hold. A token is
 * shown once, when it is issued, and never again: the API keeps only a
 * fingerprint of it. The Finance admin's alone.
 */

/** Mirrors modules/integrations/integration-rules.ts. */
export const INTEGRATION_SCOPES = [
  'packages:read',
  'subscriptions:read',
  'subscriptions:create',
  'subscriptions:update',
  'subscriptions:cancel',
  'invoices:read',
  'payments:read',
  'payments:report',
  'receipts:read',
] as const;

export const WEBHOOK_EVENTS = [
  'subscription.created',
  'subscription.updated',
  'subscription.cancelled',
  'invoice.created',
  'payment.recorded',
  'payment.reversed',
] as const;

type TokenStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

interface SystemRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  suspendedAt: string | null;
  scopes: string[];
  webhooksEnabled: boolean;
  activeTokens: number;
  lastUsedAt: string | null;
  projects: number;
  clients: number;
  liveSubscriptions: number;
  failedEvents: number;
}

interface SystemDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  suspendedAt: string | null;
  suspensionReason: string | null;
  scopes: string[];
  allowCustomPricing: boolean;
  allowedCidrs: string[];
  projects: Array<{ id: string; code: string; name: string; nameAr: string | null }>;
  clients: Array<{ id: string; code: string; companyName: string; companyNameAr: string | null; status: string }>;
  webhook: { url: string | null; events: string[]; enabled: boolean; hasSecret: boolean };
  tokens: Array<{
    id: string;
    name: string;
    hint: string;
    scopes: string[];
    status: TokenStatus;
    expiresAt: string | null;
    revokedAt: string | null;
    revocationReason: string | null;
    replacedById: string | null;
    lastUsedAt: string | null;
    lastUsedIp: string | null;
    failedAttempts: number;
    lastFailedAt: string | null;
    createdAt: string;
  }>;
  counts: { subscriptions: number; paymentsReported: number; events: Partial<Record<'PENDING' | 'DELIVERED' | 'FAILED', number>> };
  history: Array<{ id: string; action: string; occurredAt: string; actor: { fullName: string; fullNameAr: string | null } | null; newValues: any }>;
}

interface EventRow {
  id: string;
  type: string;
  resourceType: string;
  resourceId: string;
  status: 'PENDING' | 'DELIVERED' | 'FAILED';
  attempts: number;
  nextAttemptAt: string;
  lastAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

const TOKEN_TONE: Record<TokenStatus, BadgeTone> = { ACTIVE: 'success', EXPIRED: 'neutral', REVOKED: 'error' };
const EVENT_TONE: Record<EventRow['status'], BadgeTone> = { PENDING: 'info', DELIVERED: 'success', FAILED: 'error' };

const scopeLabel = (t: (key: TranslationKey) => string, scope: string) => t(`extsys.scope.${scope}` as TranslationKey);

/** The systems — Settings › External systems. */
export function ExternalSystemsPanel() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['external-systems'],
    queryFn: () => api.get<SystemRow[]>('/external-systems'),
  });
  return (
    <Card padded={false}>
      <CardHeader
        title={t('extsys.title')}
        subtitle={t('extsys.subtitle')}
        className="p-5 pb-0"
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/settings/external-systems/new')}>
            {t('extsys.new')}
          </Button>
        }
      />
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState icon={<KeyRound className="h-8 w-8" />} title={t('extsys.none')} hint={t('extsys.noneHint')} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('extsys.name')}</Th>
              <Th>{t('common.status')}</Th>
              <Th align="end">{t('extsys.tokens')}</Th>
              <Th>{t('extsys.lastUsed')}</Th>
              <Th align="end">{t('extsys.reach')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((s) => (
              <Tr key={s.id} onClick={() => navigate(`/settings/external-systems/${s.id}`)}>
                <Td>
                  {s.name}
                  <span className="block font-mono text-caption text-muted" dir="ltr">{s.code}</span>
                </Td>
                <Td>
                  <Badge tone={s.isActive ? 'success' : 'error'}>{s.isActive ? t('extsys.status.active') : t('extsys.status.suspended')}</Badge>
                  {s.failedEvents > 0 && (
                    <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">{t('extsys.failedEvents', { count: s.failedEvents })}</span>
                  )}
                </Td>
                <Td align="end" className="tabular">{s.activeTokens}</Td>
                <Td className="whitespace-nowrap text-muted">{s.lastUsedAt ? formatDateTime(s.lastUsedAt, locale) : t('extsys.neverUsed')}</Td>
                <Td align="end" className="whitespace-nowrap text-caption text-muted">
                  {t('extsys.reachValue', { projects: s.projects, clients: s.clients, subscriptions: s.liveSubscriptions })}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function BackToSystems({ id, name }: { id?: string; name?: string }) {
  const { t } = useI18n();
  return (
    <div className="mb-4">
      <Link
        to={id ? `/settings/external-systems/${id}` : '/settings?tab=external-systems'}
        className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
      >
        <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
        {name ?? t('extsys.title')}
      </Link>
    </div>
  );
}

export function ExternalSystemDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [eventPage, setEventPage] = useState(1);
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const events = useQuery({
    queryKey: ['external-system', id, 'events', eventPage],
    queryFn: () => api.get<Paginated<EventRow>>(`/external-systems/${id}/events`, { page: eventPage, pageSize: 20 }),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
    void queryClient.invalidateQueries({ queryKey: ['external-systems'] });
  };
  const reactivate = useMutation({
    mutationFn: () => api.post(`/external-systems/${id}/reactivate`),
    onSuccess: () => {
      refresh();
      toast.success(t('extsys.reactivated'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const test = useMutation({
    mutationFn: () => api.post(`/external-systems/${id}/webhook-test`),
    onSuccess: () => {
      refresh();
      toast.success(t('extsys.testSent'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const redeliver = useMutation({
    mutationFn: (eventId: string) => api.post(`/external-systems/${id}/events/${eventId}/redeliver`),
    onSuccess: () => {
      refresh();
      toast.success(t('extsys.redelivered'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !s) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <BackToSystems />
      <PageHeader
        title={s.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={s.isActive ? 'success' : 'error'}>{s.isActive ? t('extsys.status.active') : t('extsys.status.suspended')}</Badge>
            <span className="font-mono text-caption" dir="ltr">{s.code}</span>
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/settings/external-systems/${id}/edit`)}>{t('common.edit')}</Button>
            {s.isActive ? (
              <>
                <Button variant="primary" icon={<KeyRound className="h-4 w-4" />} onClick={() => navigate(`/settings/external-systems/${id}/tokens/new`)}>
                  {t('extsys.issueToken')}
                </Button>
                <Button variant="danger" onClick={() => navigate(`/settings/external-systems/${id}/suspend`)}>
                  {t('extsys.suspend')}
                </Button>
              </>
            ) : (
              <Button variant="primary" loading={reactivate.isPending} onClick={() => reactivate.mutate()}>
                {t('extsys.reactivate')}
              </Button>
            )}
          </div>
        }
      />
      {!s.isActive && (
        <Card className="mb-4 border-error/40">
          <p className="text-body-sm text-error-fg dark:text-error-dark">
            {t('extsys.suspendedSince', { date: formatDateTime(s.suspendedAt, locale), reason: s.suspensionReason ?? '' })}
          </p>
        </Card>
      )}
      {s.description && <p dir="auto" className="mb-4 whitespace-pre-wrap text-start text-body-sm text-muted">{s.description}</p>}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="grid min-w-0 grid-cols-1 content-start gap-5 lg:col-span-2">
          <Card padded={false}>
            <CardHeader title={t('extsys.tokens')} subtitle={t('extsys.tokensHint')} className="p-5 pb-0" />
            {s.tokens.length === 0 ? (
              <p className="px-5 py-4 text-body-sm text-muted">{t('extsys.noTokens')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('extsys.tokenName')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('extsys.lastUsed')}</Th>
                    <Th align="end">{t('common.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.tokens.map((token) => (
                    <Tr key={token.id}>
                      <Td className="min-w-[12rem]">
                        {token.name}
                        <span className="block font-mono text-caption text-muted" dir="ltr">{token.hint}</span>
                        <span className="block text-caption text-muted">{token.scopes.map((scope) => scopeLabel(t, scope)).join(' · ')}</span>
                      </Td>
                      <Td>
                        <Badge tone={TOKEN_TONE[token.status]}>{t(`extsys.tokenStatus.${token.status}` as TranslationKey)}</Badge>
                        {token.expiresAt && token.status === 'ACTIVE' && (
                          <span className="mt-1 block text-caption text-muted">{t('extsys.until', { date: formatDateTime(token.expiresAt, locale) })}</span>
                        )}
                        {token.revocationReason && <span className="mt-1 block text-caption text-muted">{token.revocationReason}</span>}
                      </Td>
                      <Td className="whitespace-nowrap text-caption text-muted">
                        {token.lastUsedAt ? formatDateTime(token.lastUsedAt, locale) : t('extsys.neverUsed')}
                        {token.lastUsedIp && <span className="block font-mono" dir="ltr">{token.lastUsedIp}</span>}
                        {token.failedAttempts > 0 && (
                          <span className="block text-warning-fg dark:text-warning-dark">{t('extsys.failedAttempts', { count: token.failedAttempts })}</span>
                        )}
                      </Td>
                      <Td align="end" className="whitespace-nowrap">
                        {token.status === 'ACTIVE' && s.isActive && (
                          <span className="inline-flex gap-2">
                            <Button size="sm" onClick={() => navigate(`/settings/external-systems/${id}/tokens/${token.id}/rotate`)}>
                              {t('extsys.rotate')}
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => navigate(`/settings/external-systems/${id}/tokens/${token.id}/revoke`)}>
                              {t('extsys.revoke')}
                            </Button>
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('extsys.events')} subtitle={t('extsys.eventsHint')} className="p-5 pb-0" />
            {!events.data?.items.length ? (
              <p className="px-5 py-4 text-body-sm text-muted">{t('extsys.noEvents')}</p>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('extsys.event')}</Th>
                      <Th>{t('common.status')}</Th>
                      <Th align="end">{t('extsys.attempts')}</Th>
                      <Th align="end">{t('common.actions')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.items.map((e) => (
                      <Tr key={e.id}>
                        <Td>
                          <span className="font-mono text-mono-sm" dir="ltr">{e.type}</span>
                          <span className="block text-caption text-muted">{formatDateTime(e.createdAt, locale)}</span>
                        </Td>
                        <Td>
                          <Badge tone={EVENT_TONE[e.status]}>{t(`extsys.event.${e.status}` as TranslationKey)}</Badge>
                          {e.lastError && e.status !== 'DELIVERED' && (
                            <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">{e.lastError}</span>
                          )}
                          {e.status === 'PENDING' && e.attempts > 0 && (
                            <span className="mt-1 block text-caption text-muted">{t('extsys.nextAttempt', { date: formatDateTime(e.nextAttemptAt, locale) })}</span>
                          )}
                        </Td>
                        <Td align="end" className="tabular">{e.attempts}</Td>
                        <Td align="end">
                          {e.status !== 'PENDING' && (
                            <Button size="sm" loading={redeliver.isPending} onClick={() => redeliver.mutate(e.id)}>
                              {t('extsys.redeliver')}
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Pagination page={events.data.page} pageCount={events.data.pageCount} total={events.data.total} onChange={setEventPage} />
              </>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('extsys.history')} className="p-5 pb-0" />
            <ul className="divide-y divide-[var(--line)] px-5 pb-3">
              {s.history.map((h) => (
                <li key={h.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
                  <span className="text-body-sm">
                    {HISTORY_KEYS.includes(h.action) ? t(`extsys.history.${h.action}` as TranslationKey) : h.action}
                    {h.newValues?.hint && <span className="ms-2 font-mono text-caption text-muted" dir="ltr">{h.newValues.hint}</span>}
                    <span className="ms-2 text-caption text-muted">{h.actor ? localized({ fullName: h.actor.fullName, fullNameAr: h.actor.fullNameAr }) : ''}</span>
                  </span>
                  <span className="text-caption text-muted tabular">{formatDateTime(h.occurredAt, locale)}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="grid min-w-0 grid-cols-1 content-start gap-5">
          <Card>
            <CardHeader title={t('extsys.access')} />
            <p className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('extsys.scopes')}</p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {s.scopes.length === 0 ? <span className="text-body-sm text-muted">{t('common.none')}</span> : s.scopes.map((scope) => <Badge key={scope} tone="neutral">{scopeLabel(t, scope)}</Badge>)}
            </div>
            <p className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('extsys.projects')}</p>
            <ul className="mb-3 text-body-sm">
              {s.projects.length === 0 ? <li className="text-muted">{t('common.none')}</li> : s.projects.map((p) => <li key={p.id}>{p.code} — {localized(p)}</li>)}
            </ul>
            <p className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('extsys.clients')}</p>
            <ul className="mb-3 text-body-sm">
              {s.clients.length === 0 ? (
                <li className="text-muted">{t('common.none')}</li>
              ) : (
                s.clients.map((c) => (
                  <li key={c.id}>
                    <Link className="hover:underline" to={`/clients/${c.id}`}>
                      {c.code} — {(locale === 'ar' && c.companyNameAr) || c.companyName}
                    </Link>
                  </li>
                ))
              )}
            </ul>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('extsys.customPricing')}>{s.allowCustomPricing ? t('common.yes') : t('common.no')}</Detail>
              <Detail label={t('extsys.cidrs')}>
                {s.allowedCidrs.length === 0 ? t('extsys.anywhere') : <span className="font-mono text-caption" dir="ltr">{s.allowedCidrs.join(', ')}</span>}
              </Detail>
              <Detail label={t('extsys.subscriptionsLinked')}>{s.counts.subscriptions}</Detail>
              <Detail label={t('extsys.reportedPayments')}>{s.counts.paymentsReported}</Detail>
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('extsys.webhook')} subtitle={t('extsys.webhookHint')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('extsys.webhookUrl')}>
                {s.webhook.url ? <span className="font-mono text-caption break-all" dir="ltr">{s.webhook.url}</span> : '—'}
              </Detail>
              <Detail label={t('extsys.webhooksEnabled')}>{s.webhook.enabled ? t('common.yes') : t('common.no')}</Detail>
              <Detail label={t('extsys.webhookSecret')}>{s.webhook.hasSecret ? t('extsys.secretSet') : t('extsys.secretNotSet')}</Detail>
              <Detail label={t('extsys.webhookEvents')}>
                {s.webhook.events.length === 0 ? t('extsys.everyEvent') : <span className="font-mono text-caption" dir="ltr">{s.webhook.events.join(', ')}</span>}
              </Detail>
            </dl>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => navigate(`/settings/external-systems/${id}/webhook-secret`)}>{t('extsys.newSecret')}</Button>
              {s.webhook.url && s.webhook.hasSecret && (
                <Button size="sm" icon={<Send className="h-3.5 w-3.5" />} loading={test.isPending} onClick={() => test.mutate()}>
                  {t('extsys.testWebhook')}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

const HISTORY_KEYS = [
  'external_system.created',
  'external_system.updated',
  'external_system.suspended',
  'external_system.reactivated',
  'external_system.token_issued',
  'external_system.token_revoked',
  'external_system.token_rotated',
  'external_system.webhook_secret_issued',
  'external_system.webhook_tested',
  'external_system.event_redelivered',
];

/** A list of checkboxes, each one a value of `values`. */
function CheckList<T extends string>({
  options,
  values,
  onChange,
  label,
  disabled,
}: {
  options: ReadonlyArray<{ value: T; label: string; hint?: string }>;
  values: T[];
  onChange: (values: T[]) => void;
  label?: (value: T) => string;
  disabled?: (value: T) => boolean;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((option) => (
        <Checkbox
          key={option.value}
          checked={values.includes(option.value)}
          disabled={disabled?.(option.value)}
          onChange={(e) => onChange(e.target.checked ? [...values, option.value] : values.filter((v) => v !== option.value))}
          label={
            <span>
              {label ? label(option.value) : option.label}
              {option.hint && <span className="block text-caption text-muted">{option.hint}</span>}
            </span>
          }
        />
      ))}
    </div>
  );
}

export function ExternalSystemFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const projects = useSubscriptionProjects();
  const clients = useClientOptions();
  const existing = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
    enabled: editing,
  });

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState('');
  const [customPricing, setCustomPricing] = useState(false);
  const [cidrs, setCidrs] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [webhooksEnabled, setWebhooksEnabled] = useState(false);

  useEffect(() => {
    const s = existing.data;
    if (!s) return;
    setName(s.name);
    setCode(s.code);
    setDescription(s.description ?? '');
    setScopes(s.scopes);
    setProjectIds(s.projects.map((p) => p.id));
    setClientIds(s.clients.map((c) => c.id));
    setCustomPricing(s.allowCustomPricing);
    setCidrs(s.allowedCidrs.join('\n'));
    setWebhookUrl(s.webhook.url ?? '');
    setWebhookEvents(s.webhook.events);
    setWebhooksEnabled(s.webhook.enabled);
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        scopes,
        projectIds,
        clientIds,
        allowCustomPricing: customPricing,
        allowedCidrs: cidrs.split(/\s+/).map((c) => c.trim()).filter(Boolean),
        webhookUrl: webhookUrl.trim() || null,
        webhookEvents,
      };
      return editing
        ? api.patch<SystemDetail>(`/external-systems/${id}`, { ...body, webhooksEnabled })
        : api.post<SystemDetail>('/external-systems', { ...body, code: code.trim() || undefined });
    },
    onSuccess: (system) => {
      void queryClient.invalidateQueries({ queryKey: ['external-systems'] });
      void queryClient.invalidateQueries({ queryKey: ['external-system', system.id] });
      toast.success(editing ? t('extsys.saved') : t('extsys.created'));
      navigate(`/settings/external-systems/${system.id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const fieldError = (field: string) =>
    save.error instanceof ApiError && (save.error.details as { field?: string } | undefined)?.field === field ? save.error.message : null;
  const offered = (projects.data ?? []).filter((p) => p.offered || projectIds.includes(p.id));
  const shownClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    return (clients.data ?? []).filter(
      (c) => clientIds.includes(c.id) || !q || c.companyName.toLowerCase().includes(q) || (c.companyNameAr ?? '').includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [clients.data, clientFilter, clientIds]);

  return (
    <FormPage
      title={editing ? t('extsys.editTitle', { name: existing.data?.name ?? '' }) : t('extsys.new')}
      backTo={editing ? `/settings/external-systems/${id}` : '/settings?tab=external-systems'}
      backLabel={editing ? (existing.data?.name ?? t('extsys.title')) : t('extsys.title')}
      submitLabel={t('common.save')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!name.trim()}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      width="lg"
    >
      <div className="grid gap-6">
        <FormGrid>
          <Field label={t('extsys.name')} required error={fieldError('name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} dir="auto" />
          </Field>
          <Field label={t('extsys.code')} hint={editing ? t('extsys.codeFixed') : t('extsys.codeHint')} error={fieldError('code')}>
            <Input value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} maxLength={40} dir="ltr" disabled={editing} />
          </Field>
          <Field label={t('extsys.description')} className="sm:col-span-2">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} dir="auto" />
          </Field>
        </FormGrid>

        <Field label={t('extsys.scopes')} hint={t('extsys.scopesHint')} error={fieldError('scopes')}>
          <CheckList
            options={INTEGRATION_SCOPES.map((scope) => ({ value: scope, label: scopeLabel(t, scope), hint: scope }))}
            values={scopes as (typeof INTEGRATION_SCOPES)[number][]}
            onChange={setScopes}
          />
        </Field>

        <Field label={t('extsys.projects')} hint={t('extsys.projectsHint')} error={fieldError('projectIds')}>
          {offered.length === 0 ? (
            <p className="text-body-sm text-muted">{t('extsys.noOfferedProjects')}</p>
          ) : (
            <CheckList
              options={offered.map((p) => ({ value: p.id, label: `${p.code} — ${localized(p)}` }))}
              values={projectIds}
              onChange={setProjectIds}
            />
          )}
        </Field>

        <Field label={t('extsys.clients')} hint={t('extsys.clientsHint')} error={fieldError('clientIds')}>
          <div className="grid gap-3">
            <Input placeholder={t('common.search')} aria-label={t('common.search')} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} />
            <div className="max-h-64 overflow-y-auto rounded-md border hairline p-3">
              <CheckList
                options={shownClients.map((c) => ({ value: c.id, label: `${c.code} — ${(locale === 'ar' && c.companyNameAr) || c.companyName}` }))}
                values={clientIds}
                onChange={setClientIds}
              />
            </div>
          </div>
        </Field>

        <FormGrid>
          <Field label={t('extsys.customPricing')} hint={t('extsys.customPricingHint')}>
            <Checkbox checked={customPricing} onChange={(e) => setCustomPricing(e.target.checked)} label={customPricing ? t('common.yes') : t('common.no')} />
          </Field>
          <Field label={t('extsys.cidrs')} hint={t('extsys.cidrsHint')} error={fieldError('allowedCidrs')}>
            <Textarea value={cidrs} onChange={(e) => setCidrs(e.target.value)} dir="ltr" placeholder="203.0.113.0/24" />
          </Field>
        </FormGrid>

        <Card className="bg-[var(--surface-sunken)]">
          <CardHeader title={t('extsys.webhook')} subtitle={t('extsys.webhookHint')} />
          <div className="grid gap-4">
            <Field label={t('extsys.webhookUrl')} hint={t('extsys.webhookUrlHint')} error={fieldError('webhookUrl')}>
              <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} dir="ltr" placeholder="https://" maxLength={500} />
            </Field>
            <Field label={t('extsys.webhookEvents')} hint={t('extsys.webhookEventsHint')} error={fieldError('webhookEvents')}>
              <CheckList options={WEBHOOK_EVENTS.map((e) => ({ value: e, label: e }))} values={webhookEvents as (typeof WEBHOOK_EVENTS)[number][]} onChange={setWebhookEvents} />
            </Field>
            {editing && (
              <Field
                label={t('extsys.webhooksEnabled')}
                hint={existing.data?.webhook.hasSecret ? undefined : t('extsys.secretFirst')}
                error={fieldError('webhookSecret')}
              >
                <Checkbox
                  checked={webhooksEnabled}
                  disabled={!existing.data?.webhook.hasSecret}
                  onChange={(e) => setWebhooksEnabled(e.target.checked)}
                  label={webhooksEnabled ? t('common.yes') : t('common.no')}
                />
              </Field>
            )}
          </div>
        </Card>
      </div>
    </FormPage>
  );
}

/**
 * A secret shown this once — a token, a signing secret. Copy it, say it is
 * saved, and it is gone: nothing in Finance can show it again.
 */
function SecretOnce({ label, value, note, onDone }: { label: string; value: string; note: string; onDone: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  return (
    <Card className="border-warning/50">
      <p className="mb-3 text-body-sm font-medium text-warning-fg dark:text-warning-dark">{note}</p>
      <Field label={label}>
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="min-w-0 flex-1 select-all break-all rounded-md bg-[var(--surface-sunken)] px-3 py-2 font-mono text-mono-sm"
            dir="ltr"
          >
            {value}
          </code>
          <Button
            icon={<Copy className="h-4 w-4" />}
            onClick={() =>
              void navigator.clipboard.writeText(value).then(
                () => toast.success(t('extsys.copied')),
                () => toast.error(t('extsys.copyFailed')),
              )
            }
          >
            {t('extsys.copy')}
          </Button>
        </div>
      </Field>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)} label={t('extsys.savedAck')} />
        <Button variant="primary" disabled={!saved} onClick={onDone}>
          {t('extsys.done')}
        </Button>
      </div>
    </Card>
  );
}

/** Issue a token: its name, some of the system's scopes, an expiry — then the token, once. */
export function TokenIssuePage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState('');
  const [issued, setIssued] = useState<{ token: string; hint: string } | null>(null);
  useEffect(() => {
    if (s && scopes.length === 0) setScopes(s.scopes);
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = useMutation({
    mutationFn: () => api.post<{ token: string; hint: string }>(`/external-systems/${id}/tokens`, { name: name.trim(), scopes, expiresOn: expiresOn || undefined }),
    onSuccess: (result) => {
      setIssued(result);
      void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  if (issued) {
    return (
      <div className="max-w-3xl">
        <BackToSystems id={id} name={s?.name} />
        <PageHeader title={t('extsys.tokenIssuedTitle', { name: name.trim() })} subtitle={s?.name} />
        <SecretOnce label={t('extsys.token')} value={issued.token} note={t('extsys.tokenOnce')} onDone={() => navigate(`/settings/external-systems/${id}`, { replace: true })} />
        <ApiBaseHint />
      </div>
    );
  }

  return (
    <FormPage
      title={t('extsys.issueToken')}
      subtitle={s?.name}
      backTo={`/settings/external-systems/${id}`}
      backLabel={s?.name ?? t('extsys.title')}
      submitLabel={t('extsys.tokenIssue')}
      onSubmit={() => issue.mutate()}
      submitting={issue.isPending}
      disabled={!name.trim() || scopes.length === 0}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {s && (
        <div className="grid gap-5">
          <FormGrid>
            <Field label={t('extsys.tokenName')} hint={t('extsys.tokenNameHint')} required>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} dir="auto" />
            </Field>
            <Field label={t('extsys.tokenExpires')} hint={t('extsys.tokenExpiresHint')}>
              <Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </Field>
          </FormGrid>
          <Field label={t('extsys.tokenScopes')} hint={t('extsys.tokenScopesHint')}>
            {s.scopes.length === 0 ? (
              <p className="text-body-sm text-muted">{t('extsys.noScopes')}</p>
            ) : (
              <CheckList
                options={s.scopes.map((scope) => ({ value: scope, label: scopeLabel(t, scope), hint: scope }))}
                values={scopes}
                onChange={setScopes}
              />
            )}
          </Field>
        </div>
      )}
    </FormPage>
  );
}

/** Where a system calls the API: the API's own host, api.<domain> beside sys.<domain>. */
function ApiBaseHint() {
  const { t } = useI18n();
  const base = `${window.location.origin.replace('//sys.', '//api.')}/api/v1/integrations`;
  return (
    <Card className="mt-5">
      <dl>
        <Detail label={t('extsys.apiBase')}>
          <span className="font-mono text-caption break-all" dir="ltr">{base}</span>
        </Detail>
      </dl>
      <p className="mt-2 text-caption text-muted">{t('extsys.apiBaseHint')}</p>
    </Card>
  );
}

export function TokenRotatePage() {
  const { id = '', tokenId = '' } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const token = s?.tokens.find((x) => x.id === tokenId);
  const [overlap, setOverlap] = useState('24');
  const [issued, setIssued] = useState<{ token: string; replaces: { until: string } } | null>(null);
  const rotate = useMutation({
    mutationFn: () => api.post<{ token: string; replaces: { until: string } }>(`/external-systems/${id}/tokens/${tokenId}/rotate`, { overlapHours: Number(overlap) }),
    onSuccess: (result) => {
      setIssued(result);
      void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  if (issued) {
    return (
      <div className="max-w-3xl">
        <BackToSystems id={id} name={s?.name} />
        <PageHeader title={t('extsys.rotatedTitle', { name: token?.name ?? '' })} subtitle={t('extsys.replacedUntil', { date: formatDateTime(issued.replaces.until, locale) })} />
        <SecretOnce label={t('extsys.token')} value={issued.token} note={t('extsys.tokenOnce')} onDone={() => navigate(`/settings/external-systems/${id}`, { replace: true })} />
      </div>
    );
  }

  return (
    <FormPage
      title={t('extsys.rotateTitle', { name: token?.name ?? '' })}
      subtitle={token?.hint}
      backTo={`/settings/external-systems/${id}`}
      backLabel={s?.name ?? t('extsys.title')}
      submitLabel={t('extsys.rotateSubmit')}
      onSubmit={() => rotate.mutate()}
      submitting={rotate.isPending}
      disabled={!token || token.status !== 'ACTIVE'}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <p className="mb-4 text-body-sm text-muted">{t('extsys.rotateHint')}</p>
      <Field label={t('extsys.overlap')}>
        <Select value={overlap} onChange={(e) => setOverlap(e.target.value)}>
          {['0', '1', '24', '72', '168'].map((h) => (
            <option key={h} value={h}>{h === '0' ? t('extsys.overlapNone') : t('extsys.overlapHours', { hours: h })}</option>
          ))}
        </Select>
      </Field>
    </FormPage>
  );
}

export function TokenRevokePage() {
  const { id = '', tokenId = '' } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const token = s?.tokens.find((x) => x.id === tokenId);
  const [reason, setReason] = useState('');
  const revoke = useMutation({
    mutationFn: () => api.post(`/external-systems/${id}/tokens/${tokenId}/revoke`, { reason: reason.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
      toast.success(t('extsys.revoked'));
      navigate(`/settings/external-systems/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  return (
    <FormPage
      title={t('extsys.revokeTitle', { name: token?.name ?? '' })}
      subtitle={token?.hint}
      backTo={`/settings/external-systems/${id}`}
      backLabel={s?.name ?? t('extsys.title')}
      submitLabel={t('extsys.revoke')}
      onSubmit={() => revoke.mutate()}
      submitting={revoke.isPending}
      destructive
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <p className="mb-4 text-body-sm text-muted">{t('extsys.revokeHint')}</p>
      <Field label={t('common.reason')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} dir="auto" />
      </Field>
    </FormPage>
  );
}

export function SystemSuspendPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const [reason, setReason] = useState('');
  const suspend = useMutation({
    mutationFn: () => api.post(`/external-systems/${id}/suspend`, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
      void queryClient.invalidateQueries({ queryKey: ['external-systems'] });
      toast.success(t('extsys.suspended'));
      navigate(`/settings/external-systems/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  return (
    <FormPage
      title={t('extsys.suspendTitle', { name: s?.name ?? '' })}
      backTo={`/settings/external-systems/${id}`}
      backLabel={s?.name ?? t('extsys.title')}
      submitLabel={t('extsys.suspend')}
      onSubmit={() => suspend.mutate()}
      submitting={suspend.isPending}
      disabled={!reason.trim()}
      destructive
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <p className="mb-4 text-body-sm text-muted">{t('extsys.suspendHint')}</p>
      <Field label={t('common.reason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} dir="auto" />
      </Field>
    </FormPage>
  );
}

/** A new webhook signing secret: asked for here, shown once. */
export function WebhookSecretPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['external-system', id],
    queryFn: () => api.get<SystemDetail>(`/external-systems/${id}`),
  });
  const [secret, setSecret] = useState<string | null>(null);
  const issue = useMutation({
    mutationFn: () => api.post<{ secret: string }>(`/external-systems/${id}/webhook-secret`),
    onSuccess: (result) => {
      setSecret(result.secret);
      void queryClient.invalidateQueries({ queryKey: ['external-system', id] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  if (secret) {
    return (
      <div className="max-w-3xl">
        <BackToSystems id={id} name={s?.name} />
        <PageHeader title={t('extsys.newSecret')} subtitle={s?.name} />
        <SecretOnce label={t('extsys.webhookSecret')} value={secret} note={t('extsys.secretOnce')} onDone={() => navigate(`/settings/external-systems/${id}`, { replace: true })} />
      </div>
    );
  }
  return (
    <FormPage
      title={t('extsys.newSecret')}
      subtitle={s?.name}
      backTo={`/settings/external-systems/${id}`}
      backLabel={s?.name ?? t('extsys.title')}
      submitLabel={t('extsys.newSecret')}
      onSubmit={() => issue.mutate()}
      submitting={issue.isPending}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <p className="text-body-sm text-muted">{s?.webhook.hasSecret ? t('extsys.secretReplaces') : t('extsys.secretNew')}</p>
    </FormPage>
  );
}
